import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api, { formatTRY } from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import { Sparkles, Plus, Trash2, ToggleLeft, ToggleRight, Edit, LoaderCircle, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const TYPE_LABELS = {
    early_bird: "Erken Rezervasyon",
    last_minute: "Son Dakika",
    stay_x_pay_y: "X Gece Kal Y Öde",
    long_stay: "Uzun Konaklama",
    fixed_window: "Sabit Tarih Penceresi",
};

const TYPES = Object.entries(TYPE_LABELS).map(([v, label]) => ({ v, label }));

const INPUT = "w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500";

export default function HotelPromotionsPage({ overrideHotelId, useAdminLayout, customLayout }) {
    const { user } = useAuth() || {};
    const hotelId = overrideHotelId || user?.hotel_id;
    const Layout = customLayout || (useAdminLayout ? AdminLayout : HotelLayout);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = () => {
        setLoading(true);
        api.get(`/admin/hotels/${hotelId}/promotions`)
            .then((r) => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    };
    useEffect(() => { if (hotelId) load(); /* eslint-disable-next-line */ }, [hotelId]);

    const openNew = () => setEditing({ name: "", type: "early_bird", discount_percent: 10, is_active: true, room_type_ids: [], rate_plan_ids: [] });
    const openEdit = (p) => setEditing({ ...p });
    const close = () => setEditing(null);

    const save = async () => {
        if (!editing.name) { toast.error("Promosyon adı gerekli"); return; }
        try {
            if (editing.id) {
                await api.patch(`/admin/hotels/${hotelId}/promotions/${editing.id}`, editing);
                toast.success("Güncellendi");
            } else {
                await api.post(`/admin/hotels/${hotelId}/promotions`, editing);
                toast.success("Eklendi");
            }
            close();
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata");
        }
    };

    const toggle = async (p) => {
        try { await api.post(`/admin/hotels/${hotelId}/promotions/${p.id}/toggle`); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    };

    const remove = async (p) => {
        if (!window.confirm(`"${p.name}" promosyonu silinsin mi?`)) return;
        try { await api.delete(`/admin/hotels/${hotelId}/promotions/${p.id}`); toast.success("Silindi"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    };

    return (
        <Layout title="Promosyonlar" action={
            <button data-testid="promo-add" onClick={openNew} className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> Yeni Promosyon</button>
        }>
            {loading ? <div className="flex items-center justify-center py-20"><LoaderCircle className="h-10 w-10 animate-spin text-emerald-500" /></div> : (
                <div className="space-y-3">
                    {items.length === 0 ? (
                        <div className="p-10 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                            <Sparkles className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                            <p className="text-slate-500">Henüz promosyon yok.</p>
                            <button onClick={openNew} className="mt-3 text-sm font-semibold text-emerald-600">+ İlk promosyonu ekleyin</button>
                        </div>
                    ) : items.map((p) => (
                        <div key={p.id} data-testid={`promo-row-${p.id}`} className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${p.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-slate-100 text-slate-400 dark:bg-slate-800"}`}>%{p.discount_percent}</div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-900 dark:text-white">{p.name}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    {TYPE_LABELS[p.type]}
                                    {p.min_days_before_checkin && ` · ${p.min_days_before_checkin}+ gün önce`}
                                    {p.max_days_before_checkin && ` · son ${p.max_days_before_checkin} gün`}
                                    {p.stay_nights && ` · ${p.stay_nights} gece`}
                                    {p.pay_nights && ` (${p.pay_nights} ödeme)`}
                                    {p.stay_from && ` · ${p.stay_from} → ${p.stay_to || "?"}`}
                                </div>
                            </div>
                            <button onClick={() => toggle(p)} className={`p-2 rounded-lg ${p.is_active ? "text-emerald-500" : "text-slate-400"}`} title={p.is_active ? "Aktif" : "Pasif"}>
                                {p.is_active ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                            </button>
                            <button onClick={() => openEdit(p)} className="p-2 rounded-lg text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/30"><Edit className="h-4 w-4" /></button>
                            <button onClick={() => remove(p)} className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={close}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-bold text-lg">{editing.id ? "Promosyon Düzenle" : "Yeni Promosyon"}</h2>
                            <button onClick={close} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="space-y-3">
                            <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Ad</span><input data-testid="promo-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={INPUT} /></label>
                            <label className="block">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Tür</span>
                                <select data-testid="promo-type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className={INPUT}>
                                    {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                                </select>
                            </label>
                            <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">İndirim (%)</span><input data-testid="promo-discount" type="number" min="0" max="90" value={editing.discount_percent} onChange={(e) => setEditing({ ...editing, discount_percent: Number(e.target.value) })} className={INPUT} /></label>
                            {editing.type === "early_bird" && (
                                <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Giriş&apos;ten Min N gün önce</span><input type="number" min="1" value={editing.min_days_before_checkin || 30} onChange={(e) => setEditing({ ...editing, min_days_before_checkin: Number(e.target.value) })} className={INPUT} /></label>
                            )}
                            {editing.type === "last_minute" && (
                                <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Giriş&apos;e Max N gün kala</span><input type="number" min="1" value={editing.max_days_before_checkin || 3} onChange={(e) => setEditing({ ...editing, max_days_before_checkin: Number(e.target.value) })} className={INPUT} /></label>
                            )}
                            {(editing.type === "stay_x_pay_y" || editing.type === "long_stay") && (
                                <div className="grid grid-cols-2 gap-2">
                                    <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Min konaklama (gece)</span><input type="number" min="2" value={editing.stay_nights || 4} onChange={(e) => setEditing({ ...editing, stay_nights: Number(e.target.value) })} className={INPUT} /></label>
                                    {editing.type === "stay_x_pay_y" && <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Ödenen gece</span><input type="number" min="1" value={editing.pay_nights || 3} onChange={(e) => setEditing({ ...editing, pay_nights: Number(e.target.value) })} className={INPUT} /></label>}
                                </div>
                            )}
                            {editing.type === "fixed_window" && (
                                <div className="grid grid-cols-2 gap-2">
                                    <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Konaklama Başlangıç</span><input type="date" value={editing.stay_from || ""} onChange={(e) => setEditing({ ...editing, stay_from: e.target.value })} className={INPUT} /></label>
                                    <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Konaklama Bitiş</span><input type="date" value={editing.stay_to || ""} onChange={(e) => setEditing({ ...editing, stay_to: e.target.value })} className={INPUT} /></label>
                                </div>
                            )}
                            <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Açıklama</span><textarea rows="2" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={INPUT} /></label>
                            <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="h-4 w-4 accent-emerald-500" /><span className="text-sm font-semibold">Aktif</span></label>
                        </div>
                        <div className="mt-5 flex gap-2">
                            <button onClick={close} className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold text-sm">İptal</button>
                            <button data-testid="promo-save" onClick={save} className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm">Kaydet</button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
